/**
 * KOPS-533: Sponsor Welcome Landing Page
 *
 * Route: /sponsor/welcome?company=X&contact=Y&type=Z
 *
 * The page agents see after clicking "Start Your Free Trial" in the sponsor
 * invitation email. Describes what they're getting before signup.
 */

import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faChartLine, faRobot, faUsers, faEnvelope,
  faMobileAlt, faShieldAlt, faArrowRight, faCheckCircle, faClock, faLock
} from '@fortawesome/free-solid-svg-icons';
import { colors, fonts, FONT_LINK_HREF } from '../broker-intelligence/shared/colors';

const SponsorWelcomePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const sponsorCompany = searchParams.get('company') || 'Your Sponsor Partner';
  const sponsorContact = searchParams.get('contact') || '';
  const sponsorType = searchParams.get('type') || 'sponsor';

  const sponsorLabel = {
    lender: 'Lending Partner',
    title: 'Title Partner',
    brokerage: 'Brokerage',
    warranty: 'Warranty Partner',
    coach: 'Coach',
    association: 'Association',
    vendor: 'Partner',
  }[sponsorType] || 'Partner';

  const features = [
    {
      icon: faSearch,
      title: 'AI Buyer Search Portal',
      desc: 'Your buyers get their own AI-powered MLS search portal. They find properties with AI — you close the deal. No competitor offers this.',
    },
    {
      icon: faChartLine,
      title: 'Smart CMA Reports in 35 Seconds',
      desc: 'AI-powered comparative market analysis with machine learning price prediction, comparable sales, and market insights. What takes hours manually takes seconds with AI.',
    },
    {
      icon: faRobot,
      title: 'AI Lead Coach',
      desc: 'Your AI assistant watches your pipeline and tells you who to call, what to say, and when to follow up. Automated drip campaigns, task reminders, and lead scoring.',
    },
    {
      icon: faUsers,
      title: 'Full CRM + Pipeline Management',
      desc: 'Manage leads, track deals, and close more business. Integrated with Gmail and Outlook — send emails directly from the platform.',
    },
    {
      icon: faMobileAlt,
      title: 'County Intelligence & Market Data',
      desc: 'Access Maricopa County property data, foreclosure alerts, death deed monitoring, and investor deal analysis. Find opportunities before anyone else.',
    },
    {
      icon: faEnvelope,
      title: 'Automated Follow-Up',
      desc: 'AI-powered email and SMS automation. Jasper, your AI assistant, handles initial outreach and keeps leads warm while you focus on closings.',
    },
  ];

  const stats = [
    { value: '35 sec', label: 'CMA Report Time' },
    { value: '1.7M+', label: 'MLS Listings' },
    { value: '38K+', label: 'Agent Database' },
    { value: '$0', label: 'For 30 Days' },
  ];

  return (
    <div style={{ background: '#f8f9fa', minHeight: '100vh', fontFamily: fonts.body }}>
      <link href={FONT_LINK_HREF} rel="stylesheet" />

      {/* Hero */}
      <div style={{ background: colors.navy, padding: '60px 24px 80px', textAlign: 'center' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div style={{ marginBottom: 24 }}>
            <span style={{ fontFamily: fonts.display, fontSize: 32, letterSpacing: 3, color: colors.white }}>
              KAIROS<span style={{ color: colors.gold2 }}>IQ</span>.AI
            </span>
          </div>

          {sponsorContact && (
            <div style={{
              display: 'inline-block', background: colors.navy2, border: `1px solid ${colors.navy3}`,
              borderRadius: 30, padding: '8px 20px', marginBottom: 20, fontSize: 13, color: colors.gold2,
            }}>
              Invited by <strong>{sponsorContact}</strong> at <strong>{sponsorCompany}</strong> — {sponsorLabel}
            </div>
          )}

          <h1 style={{ fontFamily: fonts.display, fontSize: 48, color: colors.white, margin: '16px 0', letterSpacing: 2, lineHeight: 1.1 }}>
            THE AI-POWERED PLATFORM<br />
            <span style={{ color: colors.gold2 }}>FOR REAL ESTATE AGENTS</span>
          </h1>

          <p style={{ color: colors.muted, fontSize: 18, lineHeight: 1.6, maxWidth: 600, margin: '0 auto 32px' }}>
            Everything you need to find leads, close deals, and grow your business — powered by artificial intelligence and backed by live MLS data.
          </p>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 32 }}>
            <button onClick={() => {
          const bid = searchParams.get('bid') || '';
          const token = searchParams.get('token') || '';
          navigate(`/agent-portal/signup?sponsor=${encodeURIComponent(sponsorCompany)}&type=${sponsorType}&bid=${bid}&token=${token}`);
        }} style={{
              background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
              border: 'none', borderRadius: 8, padding: '16px 40px', fontSize: 18,
              fontWeight: 700, color: colors.navy, cursor: 'pointer', fontFamily: fonts.body,
            }}>
              Start Your Free 30-Day Trial <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 8 }} />
            </button>
          </div>

          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', fontSize: 13, color: colors.muted }}>
            <span><FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.green, marginRight: 4 }} /> No credit card required to start</span>
            <span><FontAwesomeIcon icon={faClock} style={{ color: colors.gold2, marginRight: 4 }} /> 30 days free</span>
            <span><FontAwesomeIcon icon={faLock} style={{ color: colors.muted, marginRight: 4 }} /> Cancel anytime</span>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div style={{ background: colors.navy2, padding: '20px 24px', borderBottom: `2px solid ${colors.gold}` }}>
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 16 }}>
          {stats.map((s, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: colors.gold2, fontFamily: fonts.display, letterSpacing: 1 }}>{s.value}</div>
              <div style={{ fontSize: 11, color: colors.muted, textTransform: 'uppercase', letterSpacing: 1 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '60px 24px' }}>
        <h2 style={{ textAlign: 'center', fontFamily: fonts.display, fontSize: 36, color: colors.navy, letterSpacing: 2, marginBottom: 8 }}>
          WHAT YOU GET
        </h2>
        <p style={{ textAlign: 'center', color: '#666', fontSize: 15, marginBottom: 48 }}>
          AI Pro includes everything you need — $185/mo value, free for 30 days. Book of Business classification and MLS activation required separately.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
          {features.map((f, i) => (
            <div key={i} style={{
              background: '#fff', borderRadius: 12, padding: 28, border: '1px solid #e9ecef',
              transition: 'box-shadow 0.2s',
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
              }}>
                <FontAwesomeIcon icon={f.icon} style={{ fontSize: 20, color: colors.navy }} />
              </div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 8px' }}>{f.title}</h3>
              <p style={{ fontSize: 13, color: '#666', lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Data Privacy */}
      <div style={{ background: colors.navy, padding: '48px 24px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: 36, color: colors.gold2, marginBottom: 16 }} />
          <h2 style={{ fontFamily: fonts.display, fontSize: 28, color: colors.white, letterSpacing: 2, marginBottom: 16 }}>
            YOUR DATA STAYS YOURS
          </h2>
          <p style={{ color: colors.muted, fontSize: 15, lineHeight: 1.8, marginBottom: 24 }}>
            Your sponsor partner does <strong style={{ color: colors.white }}>NOT</strong> have access to your emails, contacts,
            CRM data, or personal communications. The only data that can be shared is your MLS production
            history (public records) — and <strong style={{ color: colors.white }}>only if you explicitly opt in</strong>.
            You can revoke access at any time.
          </p>
          {sponsorType === 'lender' && (
            <div style={{
              background: colors.navy2, borderRadius: 8, padding: 20, border: `1px solid ${colors.navy3}`,
              textAlign: 'left', fontSize: 13, color: colors.muted, lineHeight: 1.8,
            }}>
              <div style={{ fontWeight: 700, color: colors.gold2, marginBottom: 8, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
                RESPA Compliance
              </div>
              This is software access only — no marketing or settlement services. You have no referral obligations
              to the sponsoring lender. You are free to refer to any settlement service provider of your choice.
              You will be asked to acknowledge these terms during signup.
            </div>
          )}
        </div>
      </div>

      {/* Final CTA */}
      <div style={{ padding: '60px 24px', textAlign: 'center', background: '#fff' }}>
        <h2 style={{ fontFamily: fonts.display, fontSize: 36, color: colors.navy, letterSpacing: 2, marginBottom: 12 }}>
          READY TO GET STARTED?
        </h2>
        <p style={{ color: '#666', fontSize: 15, marginBottom: 32 }}>
          30-day free trial. Full AI Pro access. No obligation. Book of Business classification &amp; MLS activation purchased separately.
        </p>
        <button onClick={() => {
          const bid = searchParams.get('bid') || '';
          const token = searchParams.get('token') || '';
          navigate(`/agent-portal/signup?sponsor=${encodeURIComponent(sponsorCompany)}&type=${sponsorType}&bid=${bid}&token=${token}`);
        }} style={{
          background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
          border: 'none', borderRadius: 8, padding: '16px 48px', fontSize: 18,
          fontWeight: 700, color: colors.navy, cursor: 'pointer', fontFamily: fonts.body,
        }}>
          Start Your Free Trial <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 8 }} />
        </button>
        <p style={{ color: '#999', fontSize: 12, marginTop: 16 }}>
          Questions? Contact <a href="mailto:support@kairosiq.ai" style={{ color: colors.gold }}>support@kairosiq.ai</a>
        </p>
      </div>

      {/* Footer */}
      <div style={{ background: colors.navy, padding: '24px', textAlign: 'center' }}>
        <p style={{ color: colors.muted, fontSize: 11, margin: 0 }}>
          KairosIQ AI, Inc. | 2700 S Gilbert Rd Ste 5, Chandler, AZ 85286 |{' '}
          <a href="https://kairosiq.ai" style={{ color: colors.gold2, textDecoration: 'none' }}>kairosiq.ai</a>
        </p>
      </div>
    </div>
  );
};

export default SponsorWelcomePage;
